import { useQuery } from "@apollo/client";
import { createContext, useContext } from "react";
import {
  GetAllOrganizationTeamsQueryQuery,
  GetAllOrganizationTeamsQueryQueryVariables,
} from "types/graphql-schema";

import getAllOrganizationTeamsQuery from "@apps/artifact/graphql/get-all-organization-teams-query";
import useLabel from "@apps/use-label/use-label";
import { assertEdgesNonNull } from "@helpers/helpers";

// What decides whether a lead may delete a role. A lead can't see every
// holder, so the server says.
type DeletableRole = {
  hasCurrentHolders?: boolean | null;
  hasHistory?: boolean | null;
};

export type CareerPermissions = {
  /** Global and people admins: everything, including levels, departments,
   * company-wide competencies, who holds a role and what's written for one
   * person. */
  canManageLadder: boolean;
  /** The departments a department lead runs. Their tracks, and competencies
   * that reach only these departments, are theirs to edit. */
  ledTeamIds: readonly number[];
};

// Outside the settings page nothing narrows the controls; the API still
// checks every save.
const CareerPermissionsContext = createContext<CareerPermissions>({
  canManageLadder: true,
  ledTeamIds: [],
});

export const CareerPermissionsProvider = CareerPermissionsContext.Provider;

// Why only an admin may delete a role: someone holds it, or it has history
// (someone held it, or a review asked about it). Null when the viewer may.
const roleDeleteBlock = (canManageLadder: boolean, role: DeletableRole) => {
  if (canManageLadder) {
    return null;
  }
  if (role.hasCurrentHolders) {
    return "held";
  }
  return role.hasHistory ? "history" : null;
};

const useCareerPermissions = () => {
  const { canManageLadder, ledTeamIds } = useContext(CareerPermissionsContext);
  const label = useLabel();
  const canCreate = canManageLadder || ledTeamIds.length > 0;
  return {
    canManageLadder,
    ledTeamIds,
    canCreate,
    canEditTrack: (teamId: number | null | undefined) =>
      canManageLadder || (teamId != null && ledTeamIds.includes(teamId)),
    // A role someone holds or held, or one a review asked about, goes only
    // with an admin: the delete takes that history with it. Null when the
    // viewer may delete the role.
    roleDeleteDisabledReason: (role: DeletableRole) => {
      const block = roleDeleteBlock(canManageLadder, role);
      if (block === "held") {
        return "People hold this role. Ask an admin to move them before deleting it.";
      }
      return block === "history"
        ? `People held this role before, or a ${label("review")} asked about it. Ask an admin to delete it.`
        : null;
    },
    trackDeleteDisabledReason: (roles: readonly DeletableRole[]) => {
      const blocks = roles.map((role) =>
        roleDeleteBlock(canManageLadder, role),
      );
      if (blocks.includes("held")) {
        return "People hold roles on this track. Ask an admin to move them before deleting it.";
      }
      return blocks.includes("history")
        ? `People held roles on this track before, or a ${label("review")} asked about them. Ask an admin to delete it.`
        : null;
    },
    // A criterion or responsibility a review asked about: deleting it deletes
    // the review's question about it, which is an admin's call.
    inReviewDeleteDisabledReason: (item: { inReview?: boolean | null }) =>
      !canManageLadder && item.inReview
        ? `A ${label("review")} asked about this. Ask an admin to delete it.`
        : null,
    canEditCompetency: (competency: {
      canUpdate?: { permission: boolean } | null;
    }) => competency.canUpdate?.permission ?? canManageLadder,
  };
};

// The server refuses a lead's delete that would take past role holders or
// review questions and answers with it. Its message says why, so it shows as
// it is.
export const LEAD_DELETE_ERROR_MATCHES = [
  {
    match: "Ask an admin to",
    title: "Only an admin can do this",
    showMessage: true,
  },
];

// The departments a department lead runs, with their names: what something
// new of theirs starts in. Empty for admins, who aren't limited to any.
export const useLedDepartments = (organizationId: number) => {
  const { canManageLadder, ledTeamIds } = useContext(CareerPermissionsContext);
  const { data } = useQuery<
    GetAllOrganizationTeamsQueryQuery,
    GetAllOrganizationTeamsQueryQueryVariables
  >(getAllOrganizationTeamsQuery, {
    variables: { organizationId },
    skip: canManageLadder || ledTeamIds.length === 0,
  });
  return data?.organization
    ? assertEdgesNonNull(data.organization.teams)
        .filter(({ id }) => ledTeamIds.includes(id))
        .map(({ id, title }) => ({ id, title }))
    : [];
};

export default useCareerPermissions;
